/**
 * The namelist — the screen managers spend real time in.
 *
 * There is no sub-team to pick (D-20/D-21): the manager simply selects who is coming, and the
 * pool follows from that. Two conveniences on top, both from `docs/07-ui-screens.md`:
 *
 *   - **Bulk-select by EMS team.** A picker shortcut only — a team is not a pooling scope.
 *   - **Add Workers from other teams**, searched org-wide, because redeployment across teams is
 *     routine here (D-33). They are tagged as out-of-department, and they spend their *own*
 *     entitlement, which moves their department's figures rather than this one's (D-46).
 *     Staff cannot be added from outside; the API enforces that, and this UI does not offer it.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '@/lib/api';
import { Money } from '@/components/bits';
import { Badge, Button, Card, EmptyState, Input, Table, Td, Th } from '@/components/ui';
import { describeNonContribution } from '@/lib/policy';
import { formatDate } from '@/lib/dates';
import type { CalculatedAttendee, RosterEmployee } from '@/lib/types';
import { cn } from '@/lib/utils';

export interface NamelistProps {
  departmentId: number;
  /** The event date: service length, and therefore the cap, are measured against it. */
  eventDate: string;
  selected: number[];
  onChange: (employeeIds: number[]) => void;
  /** Per-employee figures from the calculator, keyed by employee id. */
  figures: Map<number, CalculatedAttendee>;
  disabled?: boolean;
}

export function Namelist({
  departmentId,
  eventDate,
  selected,
  onChange,
  figures,
  disabled = false,
}: NamelistProps) {
  const [search, setSearch] = useState('');
  const [outsideSearch, setOutsideSearch] = useState('');

  const roster = useQuery({
    queryKey: ['roster', departmentId],
    queryFn: () => api.employees({ department_id: departmentId, limit: 500 }),
  });

  const chosen = useMemo(() => new Set(selected), [selected]);

  /**
   * Everyone selected who is not on this department's roster.
   *
   * Kept as its own list so an added Worker cannot quietly vanish from the screen while
   * remaining in the namelist — which is what would happen if the table only rendered the
   * department's own rows.
   */
  const outsiders = useMemo(() => {
    const own = new Set((roster.data?.employees ?? []).map((employee) => employee.id));
    return selected
      .filter((id) => !own.has(id))
      .map((id) => figures.get(id))
      .filter((figure): figure is CalculatedAttendee => figure !== undefined);
  }, [roster.data, selected, figures]);

  const members = roster.data?.employees ?? [];
  const teams = useMemo(() => {
    const found = new Map<string, number[]>();
    for (const member of members) {
      const label = member.ems_team_label ?? 'No team recorded';
      found.set(label, [...(found.get(label) ?? []), member.id]);
    }
    return [...found.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [members]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter(
      (member) =>
        member.full_name.toLowerCase().includes(term) ||
        member.employee_no.toLowerCase().includes(term) ||
        (member.ems_team_label ?? '').toLowerCase().includes(term),
    );
  }, [members, search]);

  const toggle = (id: number) => {
    onChange(chosen.has(id) ? selected.filter((each) => each !== id) : [...selected, id]);
  };

  const setMany = (ids: number[], include: boolean) => {
    const next = new Set(selected);
    for (const id of ids) {
      if (include) next.add(id);
      else next.delete(id);
    }
    onChange([...next]);
  };

  return (
    <Card
      title={`Who is coming — ${selected.length} selected`}
      action={
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={disabled || visible.length === 0}
            onClick={() =>
              setMany(
                visible.map((member) => member.id),
                true,
              )
            }
          >
            Select all shown
          </Button>
          <Button
            size="sm"
            disabled={disabled || selected.length === 0}
            onClick={() => onChange([])}
          >
            Clear
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <label className="text-sm font-medium" htmlFor="namelist-search">
              Search this department
            </label>
            <Input
              id="namelist-search"
              className="mt-1.5"
              placeholder="Name, employee number or team"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        {teams.length > 1 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground text-xs">Add a whole EMS team:</span>
            {teams.map(([label, ids]) => (
              <Button
                key={label}
                size="sm"
                variant="ghost"
                disabled={disabled}
                className="border-border border"
                onClick={() => setMany(ids, true)}
              >
                {label} <span className="text-muted-foreground">({ids.length})</span>
              </Button>
            ))}
          </div>
        ) : null}

        {roster.isPending ? (
          <p className="text-muted-foreground text-sm">Loading the roster…</p>
        ) : visible.length === 0 ? (
          <EmptyState title="Nobody matches that search">
            Clear the search to see the whole department.
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th className="w-8">
                  <span className="sr-only">Coming</span>
                </Th>
                <Th>Name</Th>
                <Th>No.</Th>
                <Th>Team</Th>
                <Th numeric>Service</Th>
                <Th numeric>Cap</Th>
                <Th numeric>Used</Th>
                <Th numeric>Held</Th>
                <Th numeric>Available</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  figure={figures.get(member.id)}
                  checked={chosen.has(member.id)}
                  disabled={disabled}
                  onToggle={() => toggle(member.id)}
                />
              ))}
            </tbody>
          </Table>
        )}

        <OutsideWorkers
          departmentId={departmentId}
          eventDate={eventDate}
          search={outsideSearch}
          onSearch={setOutsideSearch}
          selected={chosen}
          disabled={disabled}
          onAdd={(id) => setMany([id], true)}
        />

        {outsiders.length > 0 ? (
          <div className="grid gap-2">
            <h3 className="text-sm font-medium">From other teams ({outsiders.length})</h3>
            <Table>
              <thead>
                <tr>
                  <Th className="w-8" />
                  <Th>Name</Th>
                  <Th>Department</Th>
                  <Th numeric>Available</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {outsiders.map((figure) => (
                  <tr key={figure.employee_id}>
                    <Td>
                      <input
                        type="checkbox"
                        checked
                        disabled={disabled}
                        aria-label={`Remove ${figure.full_name}`}
                        onChange={() => toggle(figure.employee_id)}
                        className="accent-primary size-4"
                      />
                    </Td>
                    <Td>
                      {figure.full_name}{' '}
                      <span className="text-muted-foreground text-xs">{figure.employee_no}</span>
                    </Td>
                    <Td>
                      <Badge>{figure.department_name ?? 'Unassigned'}</Badge>
                    </Td>
                    <Td numeric>
                      <Money cents={figure.available_cents} />
                    </Td>
                    <Td>
                      {figure.contributes ? null : (
                        <span className="text-muted-foreground text-xs">
                          {describeNonContribution(figure.reason)}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function MemberRow({
  member,
  figure,
  checked,
  disabled,
  onToggle,
}: {
  member: RosterEmployee;
  figure: CalculatedAttendee | undefined;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const nonContributor = figure !== undefined && !figure.contributes;
  return (
    <tr className={cn(nonContributor ? 'text-muted-foreground' : '')}>
      <Td>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-label={`${member.full_name} is coming`}
          onChange={onToggle}
          className="accent-primary size-4"
        />
      </Td>
      <Td>
        <span className={cn(nonContributor ? '' : 'font-medium')}>{member.full_name}</span>
        {member.employment_status !== 'active' ? (
          <span className="ml-2">
            <Badge tone="warn">{member.employment_status}</Badge>
          </span>
        ) : null}
        {nonContributor ? (
          // The reason inline, not a tooltip: it is the sentence that stops a manager
          // concluding the app is wrong.
          <p className="text-xs">
            {describeNonContribution(figure.reason, {
              lastDay: member.last_day ? formatDate(member.last_day) : null,
              hireDate: formatDate(member.hire_date),
            })}
          </p>
        ) : null}
      </Td>
      <Td className="tabular text-xs">{member.employee_no}</Td>
      <Td className="text-xs">{member.ems_team_label ?? '—'}</Td>
      <Td numeric className="text-xs">
        {figure ? `${figure.service_months} mo` : '—'}
      </Td>
      <Td numeric>{figure ? <Money cents={figure.cap_cents} /> : '—'}</Td>
      <Td numeric>{figure ? <Money cents={figure.used_cents} muted /> : '—'}</Td>
      <Td numeric>
        {figure && figure.held_cents > 0 ? (
          <HeldCell employeeId={member.id} heldCents={figure.held_cents} />
        ) : figure ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          '—'
        )}
      </Td>
      <Td numeric>{figure ? <Money cents={figure.available_cents} /> : '—'}</Td>
    </tr>
  );
}

/**
 * "S$25 held" — and, on request, by which proposal.
 *
 * The calculator returns the total, not the citation, and fetching every attendee's ledger to
 * label a hundred rows would be a hundred queries for a fact most rows do not have. So the
 * amount is always visible and the *reason* is one click, which is the shape of the question
 * people actually ask ("why is this person's figure lower than the others?").
 */
function HeldCell({ employeeId, heldCents }: { employeeId: number; heldCents: number }) {
  const [open, setOpen] = useState(false);
  const ledger = useQuery({
    queryKey: ['ledger', employeeId],
    queryFn: () => api.ledger(employeeId),
    enabled: open,
  });

  const holds = (ledger.data?.entries ?? []).filter((entry) => entry.entry_type === 'hold');

  return (
    <span className="inline-flex flex-col items-end">
      <button
        type="button"
        className="underline decoration-dotted underline-offset-4"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <Money cents={heldCents} />
      </button>
      {open ? (
        <span className="text-muted-foreground mt-0.5 text-xs">
          {ledger.isPending ? (
            'looking up…'
          ) : holds.length === 0 ? (
            'no open holds found'
          ) : (
            <>
              held by{' '}
              {holds.map((hold, index) => (
                <span key={hold.id}>
                  {index > 0 ? ', ' : ''}
                  {hold.proposal_id === null ? (
                    'an adjustment'
                  ) : (
                    // The ledger carries the id, not the reference number — so this links
                    // rather than printing a `PR-2026-0031` that would be a guess.
                    <Link
                      to={`/proposals/${hold.proposal_id}`}
                      className="underline underline-offset-2"
                    >
                      proposal #{hold.proposal_id}
                    </Link>
                  )}
                </span>
              ))}
            </>
          )}
        </span>
      ) : null}
    </span>
  );
}

/** Workers only, org-wide (D-33). Staff are department-scoped in both directions. */
function OutsideWorkers({
  departmentId,
  search,
  onSearch,
  selected,
  disabled,
  onAdd,
}: {
  departmentId: number;
  eventDate: string;
  search: string;
  onSearch: (value: string) => void;
  selected: Set<number>;
  disabled: boolean;
  onAdd: (employeeId: number) => void;
}) {
  const term = search.trim();
  const results = useQuery({
    queryKey: ['worker-search', term],
    queryFn: () => api.employees({ search: term, category: 'worker', limit: 25 }),
    enabled: term.length >= 2,
  });

  const candidates = (results.data?.employees ?? []).filter(
    (employee) => employee.department_id !== departmentId && !selected.has(employee.id),
  );

  return (
    <div className="border-border grid gap-2 border-t pt-4">
      <label className="text-sm font-medium" htmlFor="outside-search">
        Add Workers from other teams
      </label>
      <p className="text-muted-foreground text-xs">
        Redeployment across teams is normal, so any Worker may be added. They bring their own
        entitlement, which is drawn from their own department&rsquo;s budget (D-46).
      </p>
      <Input
        id="outside-search"
        placeholder="Search by name or employee number (at least 2 characters)"
        value={search}
        disabled={disabled}
        onChange={(event) => onSearch(event.target.value)}
      />
      {term.length >= 2 ? (
        results.isPending ? (
          <p className="text-muted-foreground text-xs">Searching…</p>
        ) : candidates.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            No Workers outside this department match that.
          </p>
        ) : (
          <ul className="grid gap-1">
            {candidates.map((employee) => (
              <li key={employee.id} className="flex items-center justify-between gap-3 text-sm">
                <span>
                  {employee.full_name}{' '}
                  <span className="text-muted-foreground text-xs">
                    {employee.employee_no} · {employee.department_name ?? 'Unassigned'}
                  </span>
                </span>
                <Button size="sm" disabled={disabled} onClick={() => onAdd(employee.id)}>
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}
